import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "../styles/AdaptiveLearning.scss";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getAllAdaptiveData, regenerateAdaptiveLearningContent } from "../helper/apicalls/adaptiveLearning";
import { getUserAiEvalDetails } from "../helper/apicalls/user";
import { checkIsAiEvalEnabled, checkIsAdmin } from "../helper/authUtils";
import GlobalLoader from "../components/GlobalLoader";
import AiEvalBreakdownPopup from "../components/common/AiEvalBreakdownPopup";

const AdaptiveLearningContent = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [topicMeta, setTopicMeta] = useState(location.state);
  const [loading, setLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // AI Eval state
  const [aiEvalData, setAiEvalData] = useState(null);
  const [aiEvalPopupOpen, setAiEvalPopupOpen] = useState(false);


  useEffect(() => {
    if (!topicMeta) {
      fetchAdaptiveData();
    }
  }, [topicId, topicMeta]);

  useEffect(() => {
    if (topicId) {
      fetchAiEval();
    }
  }, [topicId]);

  const fetchAdaptiveData = async () => {
    try {
      setLoading(true);
      const response = await getAllAdaptiveData();
      if (response && response.data) {
        const matchingTopic = response.data.find(
          (item) => item.id === topicId || item._id === topicId
        );
        if (matchingTopic) {
          setTopicMeta({
            id: matchingTopic.id || matchingTopic._id,
            page_type: matchingTopic.page_type,
            adaptive_status: matchingTopic.adaptive_status,
            completed: matchingTopic.completed,
            learning_context: matchingTopic.learning_context,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching adaptive learning data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiEval = async () => {
    try {
      const res = await getUserAiEvalDetails("adaptive_learning", topicId);
      if (res && res.is_evaluated) {
        setAiEvalData(res);
      }
    } catch {
      // silently ignore — eval may not exist yet
    }
  };

  const handleRegenerate = async () => {
    if (!topicId || isRegenerating) return;
    try {
      setIsRegenerating(true);
      await regenerateAdaptiveLearningContent(topicId);
      await fetchAdaptiveData();
      await fetchAiEval();
    } catch (err) {
      console.error("Error regenerating adaptive learning content:", err);
    } finally {
      setIsRegenerating(false);
    }
  };


  const getAiScore = () => {
    if (!aiEvalData) return null;
    try {
      const raw = aiEvalData.data;
      const record = typeof raw === "string" ? JSON.parse(raw) : raw;
      const score = record?.overall_score ?? null;
      if (score === null) return null;
      const num = parseFloat(score);
      return isNaN(num) ? null : num;
    } catch {
      return null;
    }
  };

  const aiScore = getAiScore();

  const getScoreColor = (score) => {
    if (score === null) return "#9097a6";
    if (score <= 3) return "#ef4444";
    if (score <= 7) return "#f59e0b";
    return "#10b981";
  };

  const getBreadcrumbPath = (learningContext = {}) => {
    const sequence = ["course", "subject", "topic", "subtopic"];
    return sequence
      .map((key) => {
        const node = learningContext[key];
        if (!node) return null;
        return node.name || node.title || node.display_name;
      })
      .filter(Boolean)
      .join(" > ");
  };

  if (loading || isRegenerating) return <GlobalLoader />;

  if (!topicMeta) {
    return (
      <div className="adaptive-learning-content-view custom-scrollbar">
        <div className="detail-header">
          <div className="header-content">
            <ArrowBackIcon
              className="back-icon"
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer" }}
            />
            <h1 className="detail-title">Adaptive learning content</h1>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "2rem",
          }}
        >
          <p>Adaptive learning content not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adaptive-learning-content-view custom-scrollbar">
      <div className="detail-header">
        <div className="header-content">
          <ArrowBackIcon
            className="back-icon"
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer" }}
          />
          {topicMeta.learning_context && (
            <h1 className="detail-title">
              {getBreadcrumbPath(topicMeta.learning_context)}
            </h1>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            {/* AI Score chip — shown when eval exists */}
            {aiScore !== null && (
              <button
                className="ps-row__aieval-chip evaluated"
                onClick={() => setAiEvalPopupOpen(true)}
                title="View Evaluation"
              >
                Eval: {Number.isInteger(aiScore)
                  ? `${aiScore}/10`
                  : `${aiScore.toFixed(1)}/10`}
              </button>
            )}

            {/* Regenerate button — shown only when AI Eval is enabled and user is not admin */}
            {checkIsAiEvalEnabled() && !checkIsAdmin() && (
              <button
                className="ps-row__aieval-chip regenerate"
                onClick={handleRegenerate}
                disabled={isRegenerating}
                title="Regenerate Adaptive Learning Content"
              >
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </button>
            )}
          </div>
        </div>
      </div>


      <div className="detail-scroll-container">
        <div className="adaptive-content-cards">
          <div
            className="adaptive-card adaptive-card--wide"
            onClick={() =>
              navigate(`/adaptive-learning/${topicId}/content`, {
                state: topicMeta,
              })
            }
          >
            <div className="card-title">Adaptive Learning Content</div>
            <div className="card-footer">
              <div className="card-status active">content</div>
              <div className="card-cta active">View →</div>
            </div>
          </div>

          <div
            className="adaptive-card adaptive-card--wide"
            onClick={() =>
              navigate(`/adaptive-learning/${topicId}/test`, {
                state: topicMeta,
              })
            }
          >
            <div className="card-title">Adaptive Learning Test</div>
            <div className="card-footer">
              <div className="card-status active">test</div>
              <div className="card-cta active">Start →</div>
            </div>
          </div>
        </div>
      </div>

      <AiEvalBreakdownPopup
        open={aiEvalPopupOpen}
        onClose={() => setAiEvalPopupOpen(false)}
        evalData={aiEvalData}
      />
    </div>
  );
};

export default AdaptiveLearningContent;
